import { z } from 'zod';
import { ChartType } from '@sisense/sdk-ui';
import { getQueryRecommendationsLight } from '@/services/nlq/nlq-service';
import { getDataSourceSchema } from '@/services/data-models/data-models-service';
import { generateChart } from '@/engines/build-chart/create-chart/create-chart';
import {
	recommendQueriesSchema,
	RecommendQueriesContext,
	RecommendQueriesResult,
	QueryAndChartRecommendation,
	OnIntermediateResultsCallback,
	DEFAULT_QUERY_COUNT,
	CHART_TYPE_KEYWORDS,
} from './types';
import { TOOL_NAME_RECOMMEND_QUERIES, toEngineName } from '@/constants';
import { devDebug } from '@/utils/dev-debug';

/**
 * Helper function to extract chart type preference from user prompt
 */
export function extractChartTypeFromPrompt(
	userPrompt: string,
): ChartType | undefined {
	const lowerPrompt = userPrompt.toLowerCase();
	for (const [keyword, chartType] of Object.entries(CHART_TYPE_KEYWORDS)) {
		if (lowerPrompt.includes(keyword)) {
			return chartType;
		}
	}
	return undefined;
}

/**
 * Helper function to build context-aware user prompt
 */
export function buildContextualPrompt(
	userPrompt?: string,
	existingWidgets?: string[],
): string | undefined {
	if (!userPrompt && !existingWidgets?.length) {
		return undefined;
	}

	let contextualPrompt = '';

	// Add the user's original prompt
	if (userPrompt) {
		contextualPrompt = userPrompt;
	}

	// Add context about existing widgets to avoid duplicates
	if (existingWidgets?.length) {
		// also unique the existing widgets
		const uniqueExistingWidgets = [...new Set(existingWidgets)];
		const widgetContext = `DO NOT recommend queries that are similar to these:\n${uniqueExistingWidgets
			.map((widget) => `- ${widget}`)
			.join('\n')}`;
		contextualPrompt = contextualPrompt
			? `<INSTRUCTIONS>${widgetContext}</INSTRUCTIONS>\n\n<USER-PROMPT> ${contextualPrompt}</USER-PROMPT>`
			: widgetContext;
	}

	return contextualPrompt;
}

/**
 * Standalone recommend queries engine - completely decoupled from any UI or store
 * @param params - The parameters for getting query recommendations
 * @param context - Context with tool call ID for tracking
 * @param onIntermediateResults - Optional callback for intermediate results (before chart generation)
 * @returns The recommendations result with query and chart recommendations
 */
export async function recommendQueriesEngine(
	params: z.infer<typeof recommendQueriesSchema>,
	context: RecommendQueriesContext,
	onIntermediateResults?: OnIntermediateResultsCallback,
): Promise<RecommendQueriesResult> {
	const {
		dataSourceTitle,
		queryCount = DEFAULT_QUERY_COUNT,
		userPrompt,
		existingWidgets,
	} = params;

	const defaultErrorMessage = 'Error: Failed to get recommended queries';

	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_RECOMMEND_QUERIES),
		dataSourceTitle,
		queryCount,
		userPrompt,
		existingWidgets,
		toolCallId: context.toolCallId,
	});

	// Validate required parameters
	if (!dataSourceTitle) {
		throw new Error('Error: Please specify data source title');
	}

	// Verify data source exists
	const dataSources = await getDataSourceSchema(
		dataSourceTitle,
		context.httpClient,
	);
	if (!dataSources) {
		throw new Error(
			'Error: Data source not found. Make sure to create a model first',
		);
	}

	// Build contextual prompt that includes user intent and existing widgets
	const contextualPrompt = buildContextualPrompt(userPrompt, existingWidgets);

	const recommendedQueries = await getQueryRecommendationsLight(
		dataSourceTitle,
		{
			numOfRecommendations: queryCount,
			userPrompt: contextualPrompt,
		},
		context.httpClient,
	);

	if (!recommendedQueries) {
		throw new Error(defaultErrorMessage);
	}

	// Notify intermediate results (queries without charts) if callback provided
	if (onIntermediateResults && recommendedQueries.length > 0) {
		const intermediateResults = recommendedQueries.map((query) => ({
			nlqPrompt: query.nlqPrompt,
			detailedDescription: query.detailedDescription,
		})) as QueryAndChartRecommendation[];
		onIntermediateResults(intermediateResults);
	}

	// Extract chart type preference from user prompt
	const preferredChartType = userPrompt
		? extractChartTypeFromPrompt(userPrompt)
		: undefined;

	// Generate chart for each recommended query
	const recommendedCharts = await Promise.all(
		recommendedQueries.map(async (query) => {
			// query.nlqPrompt is already data-focused, use it as queryPrompt
			// Create a simple chartPrompt based on preferred chart type or generic
			const chartPrompt = preferredChartType
				? `Create a ${preferredChartType} chart visualization`
				: 'Create a chart visualization';

			const { widgetProps } = await generateChart(
				{
					dataSourceTitle,
					queryPrompt: query.nlqPrompt,
					chartPrompt,
					chartType: preferredChartType,
					chartTitle: query.nlqPrompt,
				},
				{
					toolCallId: context.toolCallId,
					dataSourceTitle,
					isNlqV3Enabled: context.isNlqV3Enabled,
					httpClient: context.httpClient,
					openAIClient: context.openAIClient,
				},
			);

			return {
				...widgetProps,
				nlqPrompt: query.nlqPrompt,
				detailedDescription: query.detailedDescription,
			};
		}),
	);

	return {
		recommendations: recommendedCharts,
	};
}
